import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddCommentIcon from "@mui/icons-material/AddComment";
import ArchiveIcon from "@mui/icons-material/Archive";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FilterListIcon from "@mui/icons-material/FilterList";
import ForumIcon from "@mui/icons-material/Forum";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, SxProps, Theme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EmptyInboxPlaceholder from "_shared/_views/assets/empty-inbox-all-caught-up.svg";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_RowData as RowData,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import CreateForumDialog from "messaging/forums/_views/components/forum-creation-dialog";
import ConversationTable from "messaging/forums/_views/components/forums-table";
import Forum from "messaging/forums/forum";
import { NotificationTag } from "notifications/_views/style";
import NotificationsAPIService, {
  NotificationNotFoundError,
} from "notifications/entities/notification/api/notifications-api-service";
import NotificationQuery, {
  NotificationFilterParameters,
  NotificationOrderField,
  NotificationOrderParameters,
} from "notifications/entities/notification/api/request-contracts/notification-query";
import Notification from "notifications/entities/notification/notification";
import { NotificationLink, NotificationType } from "notifications/entities/notification/notification-type";
import {
  addNotification,
  getErrorLoadingNotificationsByQuery,
  getIsLoadingNotificationsByQuery,
  getLastSuccessfulNotificationResponseByInterface,
  getNotificationQueryByInterface,
  getNotificationsByQuery,
  isNotificationLoadIssue,
  NotificationLoadIssue,
  populateNotificationsByQuery,
  updateNotificationInterfaceQuery,
} from "notifications/entities/notification/store/notifications-redux-slice";
import { Action } from "notifications/values/action/action";
import { BoxType } from "notifications/values/box-type/box-type";
import TopicCategory from "notifications/values/topic-category/topic-category";
import { enqueueSnackbar } from "notistack";
import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import Individual from "marketplace/individuals/individual";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { skipToken } from "@reduxjs/toolkit/query";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Loader from "_shared/_views/loading/loader";

const NoRowsContainer = styled("div")(() => ({
  height: "100%",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));
const RowActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  width: "inherit",
}));
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const ToolbarActionButtons = styled("div")(() => ({
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const CategoryItem = styled(ListItem)(() => ({
  margin: 0,
  padding: 0,
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const AllCaughtUp = styled("img")(({ theme }) => ({
  maxHeight: "100px",
  padding: theme.spacing(2),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const FilterButtons = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: theme.spacing(1),
  "& > .MuiChip-root": {
    [theme.breakpoints.down("xl")]: {
      marginBottom: theme.spacing(0.5),
    },
    marginLeft: theme.spacing(0.5),
  },
}));
const TableTitleContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
}));
const NewForumButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  paddingRight: theme.spacing(1),
}));
const ComposeMessageButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  paddingRight: theme.spacing(1),
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const SenderCell = ({
  senderUserId,
  senderName,
  sender,
  isLoading,
}: {
  senderUserId?: Guid;
  senderName?: string;
  sender?: Individual;
  isLoading: boolean;
}) => {
  if (senderName) return <>{senderName}</>;
  if (!senderUserId) return "System";
  if (isLoading && !sender) return <>Loading...</>;
  if (!sender) return <Icon>remove</Icon>;

  return <>{sender.fullName}</>;
};

const RecipientsCell = ({
  recipientUserIds,
  recipientName,
  recipients,
  isLoading,
}: {
  recipientUserIds?: Guid[];
  recipientName?: string;
  recipients: Individual[];
  isLoading?: boolean;
}) => {
  if (recipientName) return <>{recipientName}</>;
  if (!recipientUserIds || recipientUserIds.length === 0) {
    return <Icon>remove</Icon>;
  }

  if (isLoading) return <>Loading...</>;
  if (!recipients.length) return <Icon>remove</Icon>;

  return (
    <>
      {recipients.map((recipient, index) => {
        if (!recipient) return null;
        return (
          <React.Fragment key={recipient?.id.value}>
            {recipient.fullName}
            {index < recipients.length - 1 && ", "}
          </React.Fragment>
        );
      })}
    </>
  );
};

const columnHelper = createMRTColumnHelper<NotificationType | NotificationLoadIssue>();

export default function CommunicationsPage() {
  const [activeTab, setActiveTab] = React.useState(BoxType.Inbox);
  const [filterByActionRequired, setFilterByActionRequired] = React.useState<boolean | undefined>();
  const [filterByUnread, setFilterByUnread] = React.useState<boolean | undefined>();
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const routeParams = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const dispatch = useAttorneyHubDispatch();
  const { openDialog } = useDialog();
  const isSmlDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isMedDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const interfaceName = "communicationsPage";

  const notificationQuery = useSelector(getNotificationQueryByInterface(interfaceName));
  const defaultQuery = useMemo(() => {
    return new NotificationQuery({
      filterParameters: new NotificationFilterParameters({
        query: globalFilter,
        boxType: activeTab,
        isActionRequired: filterByActionRequired,
        isUnread: filterByUnread,
      }),
      orderParameters: new NotificationOrderParameters({
        orderBy: NotificationOrderField.CreatedDate,
        orderDir: OrderDirection.Descending,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 25 }),
    });
  }, [globalFilter, activeTab, filterByActionRequired, filterByUnread]);

  const effectiveQuery = notificationQuery ?? defaultQuery;
  const queryResponse = useSelector(getNotificationsByQuery(effectiveQuery));
  const queryIsLoading = useSelector(getIsLoadingNotificationsByQuery(effectiveQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingNotificationsByQuery(effectiveQuery));
  const lastSuccessfulResponse = useSelector(getLastSuccessfulNotificationResponseByInterface(interfaceName));
  const tableNotifications = queryResponse ?? lastSuccessfulResponse;

  const userIdsToLoad = useMemo(() => {
    return getValidNotificationUserIds(tableNotifications);
  }, [tableNotifications]);

  const individualQuery = useGetIndividualsByUserIdsQuery(userIdsToLoad.size ? { userIds: userIdsToLoad } : skipToken);

  const individuals = useMemo(
    () => Individual.fromJSONArray(individualQuery.data, { allowEmpty: true }) ?? [],
    [individualQuery.data],
  );

  const openCreateForumDialog = useCallback(() => {
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

    openDialog({
      onClose: () => void navigate(parentPath.pathname),
      resetStack: true,
      component: (
        <CreateForumDialog
          onCancel={() => navigate(parentPath.pathname)}
          onForumCreated={(newForum: Forum) => {
            if (!newForum?.id) {
              navigate(parentPath.pathname);
              return;
            }
            navigate(`/communications/forums/${newForum.id.value}`);
            enqueueSnackbar(`Forum ${newForum.name} created`, { variant: "success" });
          }}
        />
      ),
      title: "Create Conversation",
      MuiProps: {
        maxWidth: "md",
      },
    });
  }, [navigate, openDialog]);

  const openMessageDialog = useCallback(
    (id?: Guid) => {

      const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);

      openDialog({
        title: id ? "Message" : "Compose Message",
        resetStack: true,
        MuiProps: {
          fullWidth: true,
          maxWidth: "md",
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
          paddingBottom: 0,
        },
        component: <ChatDialog forumId={id} />,
        onClose: () => void navigate(parentPath.pathname),
      });
    },
    [navigate, openDialog],
  );

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!notificationQuery) return;
    if (queryIsLoading) return;
    if (queryResponse !== undefined) return;

    dispatch(
      populateNotificationsByQuery({
        session,
        query: notificationQuery,
        interfaceName,
      }),
    );
  }, [dispatch, session, interfaceName, notificationQuery, queryIsLoading, queryResponse]);

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    if (routeParams.pageTab) {
      setActiveTab(routeParams.pageTab as BoxType);
    } else {
      navigate(`/communications/${BoxType.Inbox}`);
      return;
    }

    if (routeParams.action === "create") {
      openCreateForumDialog();
      return;
    }
    if (routeParams.action === "compose") {
      openMessageDialog();
      return;
    }
    if (routeParams.pageTab === BoxType.Forums) {
      if (routeParams.id) {
        openMessageDialog(new Guid(routeParams.id));
      } else if (routeParams.action) {
        openMessageDialog(new Guid(routeParams.action));
      }
    }
    const filterUnread = searchParams.has("unread") ? searchParams.get("unread") === "true" : undefined;
    const filterActionRequired = searchParams.has("actionRequired")
      ? searchParams.get("actionRequired") === "true"
      : undefined;
    setFilterByUnread(filterUnread);
    setFilterByActionRequired(filterActionRequired);

    const notificationParams = new NotificationFilterParameters({
      query: globalFilter,
      boxType: activeTab,
      isActionRequired: filterActionRequired,
      isUnread: filterUnread,
      type: columnFilters.find((filter) => filter.id === "type")?.value as string,
      from: columnFilters.find((filter) => filter.id === "sender")?.value as string,
      to: columnFilters.find((filter) => filter.id === "recipients")?.value as string,
      description: columnFilters.find((filter) => filter.id === "description")?.value as string,
      minCreatedDate: columnFilters.find((filter) => filter.id === "minCreatedDate")?.value as AHDate,
    });
    const notificationOrderParameters = new NotificationOrderParameters({
      orderBy: sorting.length > 0 ? (sorting[0].id as NotificationOrderField) : NotificationOrderField.CreatedDate,
      orderDir: sorting.length > 0 && !sorting[0].desc ? OrderDirection.Ascending : OrderDirection.Descending,
    });
    const paginationParameters = PaginationParameters.create(pagination);
    const newQuery = new NotificationQuery({
      filterParameters: notificationParams,
      orderParameters: notificationOrderParameters,
      paginationParameters: paginationParameters,
    });
    if (newQuery.isEqualTo(notificationQuery)) {
      return;
    }

    dispatch(
      updateNotificationInterfaceQuery({
        interfaceName: "communicationsPage",
        query: newQuery,
      }),
    );
  }, [
    routeParams,
    routeParams.action,
    searchParams,
    pagination.pageIndex,
    pagination.pageSize,
    activeTab,
    sorting,
    globalFilter,
    columnFilters,
    filterByUnread,
    filterByActionRequired,
    session.user?.legalEntityId,
    pagination,
    notificationQuery,
    dispatch,
    navigate,
    openCreateForumDialog,
    openMessageDialog,
  ]);

  async function navigateToTarget(notification: NotificationType | NotificationLoadIssue) {
    if (isNotificationLoadIssue(notification)) {
      return;
    }
    try {
      if (activeTab === BoxType.Inbox) {
        await new NotificationsAPIService(session).markNotificationAsSeen(notification);
      }
      navigate(NotificationLink(notification));
    } catch (error) {
      console.error(error);
    }
  }

  async function markMultipleSeen() {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));

    try {
      const notifications = selectedRowIds.map((id) => ({ id }));
      const updatedNotifications = await new NotificationsAPIService(session).markNotificationsAsSeen(notifications);
      if (updatedNotifications) {
        for (const updatedNotification of updatedNotifications) {
          dispatch(addNotification(updatedNotification));
        }
      }
      setRowSelection({});
    } catch (error) {
      if (error instanceof NotificationNotFoundError) {
        console.error("Unable to mark a deleted notification as read, refreshing to remove", error.id);
      } else {
        console.error(error);
      }
    }
  }

  async function markMultipleUnseen() {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));
    try {
      const notifications = selectedRowIds.map((id) => ({ id }));
      const updatedNotifications = await new NotificationsAPIService(session).markNotificationsAsUnSeen(notifications);
      if (updatedNotifications) {
        for (const updatedNotification of updatedNotifications) {
          dispatch(addNotification(updatedNotification));
        }
      }
      setRowSelection({});
    } catch (error) {
      if (error instanceof NotificationNotFoundError) {
        console.error("Unable to mark a deleted notification as unread, refreshing to remove", error.id);
      } else {
        console.error(error);
      }
    }
  }

  async function archiveMultiple() {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));

    try {
      for (const selectedRowId of selectedRowIds) {
        try {
          await new NotificationsAPIService(session).archiveNotification({ id: selectedRowId });
        } catch (error) {
          if (error instanceof NotificationNotFoundError) {
            console.error("Unable to archive a deleted notification, refreshing to remove ", error.id);
            continue;
          }
          throw error;
        }
      }
      setRowSelection({});
    } catch (error) {
      console.error(error);
    }
  }

  async function unArchiveMultiple() {
    const selectedRowIds: Guid[] = Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => new Guid(id));

    try {
      for (const selectedRowId of selectedRowIds) {
        try {
          await new NotificationsAPIService(session).unArchiveNotification({ id: selectedRowId });
        } catch (error) {
          if (error instanceof NotificationNotFoundError) {
            console.error("Unable to un-archive a deleted notification, refreshing to remove ", error.id);
            continue;
          }
          console.error(error);
          return;
        }
      }

      setRowSelection({});
    } catch (error) {
      console.error(error);
    }
  }

  const renderCreatedDateColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue): React.ReactNode => {
      if (isNotificationLoadIssue(notification)) {
        return null;
      }
      if (!notification.createdDate) return null;
      return <time>{notification.createdDate.format("MM/DD/YY hh:mm A")}</time>;
    },
    [],
  );

  const renderCategoryColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => {
      if (isNotificationLoadIssue(notification)) {
        return (
          <CategoryItem dense>
            <ListItemIcon>
              <Icon color="error">error</Icon>
            </ListItemIcon>
            <ListItemText primary={<strong style={{ fontWeight: "normal" }}>Unknown</strong>} />
          </CategoryItem>
        );
      }

      const topicCategory: TopicCategory = notification?.topic?.category ?? "";

      return (
        <CategoryItem dense>
          <ListItemIcon>
            {(topicCategory === TopicCategory.Messaging.Message ||
              topicCategory === TopicCategory.Messaging.ForumInvitation) && <MessageIcon />}
            {topicCategory === TopicCategory.Work.Proposal && !notification.actionRequired && <PersonAddIcon />}
            {topicCategory === TopicCategory.Users.NetworkConnection && <AccountTreeIcon />}
            {topicCategory === TopicCategory.Users.NetworkInvitation && <AccountTreeIcon />}
            {topicCategory === TopicCategory.Work.Proposal &&
              notification.actionRequired &&
              notification.actionRequired !== Action.Review && <AssignmentIcon />}
            {topicCategory === TopicCategory.Work.Proposal && notification.actionRequired === Action.Review && (
              <PageviewIcon />
            )}
            {topicCategory === TopicCategory.Work.Agreement && <WorkIcon />}
            {topicCategory === TopicCategory.Marketplace.Team && <GroupIcon />}
            {topicCategory === TopicCategory.LegalEntities.EntityMember && <PersonIcon />}
            {topicCategory === TopicCategory.Marketplace.TeamInvitation && <GroupAddIcon />}
            {topicCategory === TopicCategory.LegalEntities.LegalEntity && <SupervisorAccountIcon />}
          </ListItemIcon>
          <ListItemText
            primary={
              <strong
                style={{ fontWeight: Notification.isSeenByUser(notification, session.user?.id) ? "normal" : "bold" }}
              >
                {topicCategory === TopicCategory.Messaging.Message
                  ? "New Message"
                  : (Config.notificationTypes[topicCategory as keyof typeof Config.notificationTypes] ??
                    "Notification")}
              </strong>
            }
            secondary={
              activeTab === BoxType.Inbox &&
              (Notification.isSeenByUser(notification, session.user?.id)
                ? `Read at ${Notification.seenByUserAt(notification, session.user?.id)?.format("MM/DD/YY hh:mm A")}`
                : "Unread notification")
            }
          />
        </CategoryItem>
      );
    },
    [activeTab, session.user?.id],
  );

  const renderDescriptionColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => {
      if (isNotificationLoadIssue(notification)) {
        return <Typography color="error">There was an issue loading this notification&apos;s details.</Typography>;
      }
      const tag = Notification.userIsRecipient(notification, session.user?.id) ? (
        <NotificationTag size="small" label="Received" />
      ) : (
        <NotificationTag size="small" label="Sent" />
      );
      return (
        <>
          {activeTab === BoxType.Archive && tag}
          {notification.message?.value}
        </>
      );
    },
    [activeTab, session.user?.id],
  );

  const renderFromColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => {
      if (isNotificationLoadIssue(notification)) return null;

      const senderName =
        "messageInfo" in notification ? notification?.messageInfo?.senderName?.value : notification?.senderName?.value;
      const sender = individuals?.find((individual) => {
        return individual.userId.isEqualTo(notification.senderUserId);
      });
      const isLoading = individualQuery.isLoading;

      return (
        <SenderCell
          senderUserId={notification.senderUserId}
          senderName={senderName}
          sender={sender}
          isLoading={isLoading}
        />
      );
    },
    [individuals, individualQuery.isLoading],
  );

  const renderToColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => {
      if (isNotificationLoadIssue(notification)) return null;

      const recipients = notification.recipients?.map((r) => r.userId);
      if (!recipients?.length) return <Icon>remove</Icon>;

      return (
        <RecipientsCell
          recipientUserIds={recipients}
          recipientName={notification.recipientName?.value}
          recipients={individuals}
          isLoading={individualQuery.isLoading}
        />
      );
    },
    [individuals, individualQuery.isLoading],
  );
  const renderInboxRowActions = (notification: NotificationType | NotificationLoadIssue) => {
    if (!(notification instanceof Notification)) return null;
    return (
      <RowActionsContainer>
        {notification.isSeenByUser(session.user?.id) ? (
          <Tooltip title="Mark as Unread">
            <span>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  void markAsUnread(notification);
                }}
              >
                <MarkEmailReadIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Mark as Read">
            <span>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  void markAsRead(notification);
                }}
              >
                <MarkEmailReadIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Archive">
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                void archive(notification);
              }}
            >
              <ArchiveIcon />
            </IconButton>
          </span>
        </Tooltip>
      </RowActionsContainer>
    );
  };

  const renderArchiveRowActions = (communication: NotificationType | NotificationLoadIssue) => {
    if (isNotificationLoadIssue(communication)) {
      return null;
    }
    return (
      <RowActionsContainer>
        <Tooltip title="Restore from Archive">
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                void unArchive(communication);
              }}
            >
              <UnarchiveIcon />
            </IconButton>
          </span>
        </Tooltip>
      </RowActionsContainer>
    );
  };

  async function markAsRead(communication: NotificationType) {
    if (!communication || communication?.seenByUserAt(session.user?.id)) return;
    try {
      const updatedNotification = await new NotificationsAPIService(session).markNotificationAsSeen(communication);
      if (!updatedNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(updatedNotification));
    } catch (error) {
      if (error instanceof NotificationNotFoundError) {
        console.error("Unable to mark a deleted notification as read ", error.id);
      } else {
        console.error(error);
      }
    }
  }

  async function markAsUnread(communication: NotificationType) {
    if (!communication?.seenByUserAt(session.user?.id)) return;
    try {
      const updatedNotification = await new NotificationsAPIService(session).markNotificationAsUnSeen(communication);
      if (!updatedNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(updatedNotification));
    } catch (error) {
      if (error instanceof NotificationNotFoundError) {
        console.error("Unable to mark a deleted notification as unread, refreshing to remove ", error.id);
      } else {
        console.error(error);
      }
    }
  }

  async function archive(communication: NotificationType) {
    if (!communication) return;
    try {
      await new NotificationsAPIService(session).archiveNotification(communication);
    } catch (error) {
      if (error instanceof NotificationNotFoundError) {
        console.error("Unable to archive a deleted notification, refreshing to remove ", error.id);
      } else {
        console.error(error);
      }
    }
  }

  async function unArchive(communication: NotificationType) {
    if (!communication) return;
    try {
      await new NotificationsAPIService(session).unArchiveNotification(communication);
    } catch (error) {
      if (error instanceof NotificationNotFoundError) {
        console.error("Unable to un-archive a deleted notification, refreshing to remove ", error.id);
      } else {
        console.error(error);
      }
    }
  }

  const columns = useMemo(() => {
    const cols = [];

    if (activeTab !== BoxType.Forums) {
      cols.push(
        columnHelper.display({
          header: "Type",
          id: "type",
          enableSorting: false,
          Cell: ({ row }) => renderCategoryColumn(row.original),
        }),
        columnHelper.display({
          header: "From",
          id: "sender",
          enableSorting: false,
          Cell: ({ row }) => renderFromColumn(row.original),
        }),
        columnHelper.display({
          header: "To",
          id: "recipients",
          enableSorting: false,
          Cell: ({ row }) => renderToColumn(row.original),
        }),
        columnHelper.display({
          header: "Description",
          id: "description",
          enableSorting: false,
          Cell: ({ row }) => renderDescriptionColumn(row.original),
        }),
      );
    }

    cols.push(
      columnHelper.display({
        header: "Created",
        id: "createdDate",
        Cell: ({ row }) => renderCreatedDateColumn(row.original),
      }),
    );

    return cols;
  }, [
    activeTab,
    renderCategoryColumn,
    renderFromColumn,
    renderToColumn,
    renderDescriptionColumn,
    renderCreatedDateColumn,
  ]);

  function getRowClickTheme(notification: NotificationType | NotificationLoadIssue): SxProps<Theme> | undefined {
    if (activeTab === BoxType.Archive) {
      return {
        cursor: "",
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      };
    }
    if (isNotificationLoadIssue(notification)) {
      return {
        cursor: "pointer",
        backgroundColor: "lightred",
      };
    }
    return {
      cursor: "pointer",
      backgroundColor: Notification.isSeenByUser(notification, session.user?.id) ? "rgba(0, 0, 0, 0.04)" : "inherit",
    };
  }

  const renderTopToolbarCustomActions = <T extends RowData>(
    props: { table: TableInstance<T> },
    additional?: JSX.Element | undefined,
    filters?: JSX.Element | undefined,
  ): JSX.Element => {
    const { table } = props;

    return (
      <TableTopToolbarContainer>
        <TableTitleContainer>
          <TableTitle variant="h4">Communications</TableTitle>
          {isSmlDisplaySize && (
            <div>
              <ComposeMessageButton color="primary" onClick={() => navigate("/communications/forums/compose")}>
                <AddCommentIcon fontSize="large" />
              </ComposeMessageButton>
              <NewForumButton color="primary" onClick={() => navigate("/communications/forums/compose")}>
                <ForumIcon fontSize="large" />
              </NewForumButton>
            </div>
          )}
        </TableTitleContainer>
        <LowerTopToolbarContainer>
          <Tabs
            value={activeTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_event, newValue) => navigate(`/communications/${newValue}`)}
          >
            <Tab value={BoxType.Inbox} label="Inbox" />
            <Tab value={BoxType.Outbox} label="Sent" />
            <Tab value={BoxType.Forums} label="Conversations" />
            <Tab value={BoxType.Archive} label="Archived" />
          </Tabs>
          {additional}
          {activeTab === BoxType.Inbox && (
            <FilterButtons>
              <Chip
                icon={<FilterListIcon />}
                label="Unread"
                color="primary"
                variant={filterByUnread ? "filled" : "outlined"}
                onClick={() => {
                  setFilterByUnread((prevState) => !prevState);
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    if (filterByUnread) {
                      params.delete("unread");
                    } else {
                      params.set("unread", "true");
                    }
                    return params;
                  });
                }}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Action Required"
                color="primary"
                variant={filterByActionRequired ? "filled" : "outlined"}
                onClick={() => {
                  setFilterByActionRequired((prevState) => !prevState);
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    if (filterByActionRequired) {
                      params.delete("actionRequired");
                    } else {
                      params.set("actionRequired", "true");
                    }
                    return params;
                  });
                }}
              />
            </FilterButtons>
          )}
        </LowerTopToolbarContainer>
        {filters}
        {isMedDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    );
  };

  const tableOptions: MRT_TableOptions<NotificationType | NotificationLoadIssue> = {
    columns,
    data: tableNotifications?.data ?? [],
    rowCount: tableNotifications?.headers.totalElements ?? 0,
    enableRowSelection: activeTab !== BoxType.Forums,
    enableRowActions: activeTab !== BoxType.Forums && activeTab !== BoxType.Outbox,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        void navigateToTarget(row.original);
      },
      sx: getRowClickTheme(row.original),
    }),
    muiTableProps: () => ({
      sx: {
        height: tableNotifications?.data ? "initial" : "100%",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMedDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <ToolbarActionButtons>
              <Button
                color="primary"
                startIcon={<AddCommentIcon />}
                onClick={() => navigate("/communications/forums/compose")}
              >
                Compose Message
              </Button>
              <Button
                color="primary"
                startIcon={<ForumIcon />}
                onClick={() => navigate("/communications/forums/create")}
              >
                New Conversation
              </Button>
            </ToolbarActionButtons>
          </ToolbarButtonsContainer>
        )}
        {isMedDisplaySize && !isSmlDisplaySize && (
          <>
            <ComposeMessageButton color="primary" onClick={() => navigate("/communications/forums/compose")}>
              <AddCommentIcon fontSize="large" />
            </ComposeMessageButton>
            <NewForumButton color="primary" onClick={() => navigate("/communications/forums/create")}>
              <ForumIcon fontSize="large" />
            </NewForumButton>
          </>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitleContainer>
            <TableTitle variant="h4">Communications</TableTitle>
            {isSmlDisplaySize && (
              <div>
                <ComposeMessageButton color="primary" onClick={() => navigate("/communications/forums/compose")}>
                  <AddCommentIcon fontSize="large" />
                </ComposeMessageButton>
                <NewForumButton color="primary" onClick={() => navigate("/communications/forums/compose")}>
                  <ForumIcon fontSize="large" />
                </NewForumButton>
              </div>
            )}
          </TableTitleContainer>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/communications/${newValue}`)}
            >
              <Tab value={BoxType.Inbox} label="Inbox" />
              <Tab value={BoxType.Outbox} label="Sent" />
              <Tab value={BoxType.Forums} label="Conversations" />
              <Tab value={BoxType.Archive} label="Archived" />
            </Tabs>
            {activeTab === BoxType.Inbox && (
              <FilterButtons>
                <Chip
                  icon={<FilterListIcon />}
                  label="Unread"
                  color="primary"
                  variant={filterByUnread ? "filled" : "outlined"}
                  onClick={() => {
                    setFilterByUnread((prevState) => !prevState);
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (filterByUnread) {
                        params.delete("unread");
                      } else {
                        params.set("unread", "true");
                      }
                      return params;
                    });
                  }}
                />
                <Chip
                  icon={<FilterListIcon />}
                  label="Action Required"
                  color="primary"
                  variant={filterByActionRequired ? "filled" : "outlined"}
                  onClick={() => {
                    setFilterByActionRequired((prevState) => !prevState);
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (filterByActionRequired) {
                        params.delete("actionRequired");
                      } else {
                        params.set("actionRequired", "true");
                      }
                      return params;
                    });
                  }}
                />
              </FilterButtons>
            )}
          </LowerTopToolbarContainer>
          {isMedDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    renderRowActions: ({ row }) => {
      if (activeTab === BoxType.Archive) {
        return renderArchiveRowActions(row.original);
      }
      if (activeTab === BoxType.Inbox) {
        return renderInboxRowActions(row.original);
      }
    },
    renderEmptyRowsFallback: () => {
      if (queryIsLoading || tableNotifications === undefined) return;

      return (
        <NoRowsContainer>
          <AllCaughtUp alt="All Caught Up" src={EmptyInboxPlaceholder} />
        </NoRowsContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnOrder: [
        "type",
        "sender",
        "recipients",
        "description",
        "message",
        "conversationName",
        "createdDate",
        "actions",
      ],
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiTableBodyProps: {
      sx: {
        height: "100%",
        "& tr.mrt-table-body-row": {
          height: "100%",
        },
        "& td.mrt-table-body-cell": {
          height: "100%",
        },
      },
    },
    muiToolbarAlertBannerProps: errorLoadingQuery
      ? {
          color: "error",
          title: errorLoadingQuery || "",
        }
      : {
          action: (
            <AlertActions>
              {activeTab === BoxType.Archive && (
                <Button color="primary" startIcon={<UnarchiveIcon />} onClick={unArchiveMultiple}>
                  Un-Archive
                </Button>
              )}
              {activeTab === BoxType.Inbox && (
                <>
                  <Button color="primary" startIcon={<MarkEmailReadIcon />} onClick={markMultipleSeen}>
                    Mark Read
                  </Button>
                  <Button color="primary" startIcon={<MarkEmailUnreadIcon />} onClick={markMultipleUnseen}>
                    Mark Unread
                  </Button>
                </>
              )}
              {activeTab !== BoxType.Archive && (
                <Button color="primary" startIcon={<ArchiveIcon />} onClick={archiveMultiple}>
                  Archive
                </Button>
              )}
            </AlertActions>
          ),
        },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      globalFilter,
      isLoading: queryIsLoading,
      pagination,
      showAlertBanner: Boolean(errorLoadingQuery),
      showProgressBars: queryIsLoading,
      sorting,
      rowSelection,
      showSkeletons: queryIsLoading && !tableNotifications,
    },
  };

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToCommunications) return <NotAuthorized />;

  if (activeTab === BoxType.Forums) {
    return <ConversationTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />;
  } else return <MRTNoMemo<NotificationType | NotificationLoadIssue> options={tableOptions} />;
}

function getValidNotificationUserIds(
  tableNotifications: PaginatedResponse<NotificationLoadIssue | NotificationType> | undefined,
): Set<string> {
  const validNotifications: NotificationType[] = [];
  if (!tableNotifications) return new Set();

  tableNotifications.data.forEach((notification) => {
    if (!isNotificationLoadIssue(notification)) {
      validNotifications.push(notification);
    }
  });

  const uniqueSenderIds = new Set(
    validNotifications
      .map((notification: NotificationType) => notification.senderUserId?.value)
      .filter((senderId) => senderId !== undefined),
  );
  const uniqueRecipientIds = tableNotifications
    ? new Set(
        validNotifications.flatMap((notification: NotificationType) =>
          notification.recipients.map((recipient) => recipient.userId.value).filter((userId) => userId !== undefined),
        ),
      )
    : new Set<string>();

  return new Set([...uniqueSenderIds, ...uniqueRecipientIds]);
}
